import { Modal } from "../ui/components/Modal";
import { Modal as BsModal } from "bootstrap";
import { isSubscriptionGateEnabled } from "../utils/aiAccess";

export class SubscriptionModal extends Modal {
    init() {
        this.bsModal = new BsModal(this.element);
        this.contentLoaded = false;
        this.contentLoading = false;

        this.includeComponents({
            ButtonSub: "document@#ButtonSub",
        });

        if (!Number(this.available_region)) {
            this.includeComponents({
                regionPaymentModal: "document@#regionPaymentModal",
            });
        }

        this.ButtonSub?.on("click", () => this.show());

        this.on("hide", () => this.resetToggle());
        this.on("show.bs.modal", (e) => this.handleShow(e));
        this.on("hidden.bs.modal", () => this.handleHidden());
    }

    async ensureContent() {
        if (this.contentLoaded) return;

        const url = this.element.dataset.contentUrl;
        const response = await fetch(url);
        const html = await response.text();

        this.element.innerHTML = html;
        this.contentLoaded = true;

        // #SubscriptionModal статически пуст — контент (включая .modal-dialog)
        // грузится сюда AJAX-ом. Но при init() Bootstrap уже закэшировал
        // this._dialog = null (тогда .modal-dialog ещё не было). Без пересоздания
        // инстанса _showElement вызывает querySelector на null и падает
        // (в Safari: "Can only call Element.querySelector on instances of Element").
        // Пересоздаём модалку, чтобы Bootstrap перечитал .modal-dialog из контента.
        this.bsModal.dispose();
        this.bsModal = new BsModal(this.element);

        const toggleInput = this.element.querySelector('[data-tbx-name="toggle"]');
        if (toggleInput) {
            this.toggle = { element: toggleInput, on: (evt, cb) => toggleInput.addEventListener(evt, cb) };
            toggleInput.addEventListener("change", () => this.handleToggleChange());
        }

        if (!this.toggle) {
            this.addClass("active");
            this.updatePrices(true);
        }

        this.setInitialToggleState();

        this.findAll("a.subscription-button").forEach((button) => {
            button.addEventListener("click", (event) =>
                this.handlePayment(event, button)
            );
        });
    }

    show(relatedTarget) {
        if (this.contentLoading) return;
        if (!this.contentLoaded) {
            this.contentLoading = true;
            this.ensureContent()
                .then(() => {
                    super.show(relatedTarget);
                })
                .finally(() => {
                    this.contentLoading = false;
                });
            return;
        }
        super.show(relatedTarget);
    }

    initTitles() {
        if (this.titleEl) return;
        this.titleEl = this.element.querySelector(".title-subscription");
        if (!this.titleEl) return;
        this.defaultTitle =
            this.titleEl?.dataset?.defaultTitle ||
            this.titleEl?.textContent?.trim() ||
            "";
        this.aiTitle = this.titleEl?.dataset?.aiTitle || this.defaultTitle;
    }

    handleShow(e) {
        // Модалка статически пуста: .modal-dialog приезжает AJAX-ом в ensureContent().
        // Открытие через data-bs-toggle="modal" идёт мимо нашего show(), и Bootstrap
        // падает в _showElement на this._dialog === null. Отменяем такое открытие
        // и запускаем свой show(), который сначала догрузит контент.
        if (!this.contentLoaded) {
            e?.preventDefault();
            this.show(e?.relatedTarget);
            return;
        }

        this.initTitles();
        if (!this.titleEl) return;

        const relatedTarget = e?.relatedTarget;
        const isFromToolbar =
            relatedTarget &&
            (relatedTarget.closest?.('[data-tbx-name="toolbar__translate"]') ||
                relatedTarget.closest?.(
                    '[data-tbx-name="toolbar__ai_templates"]'
                ));
        const isMarkedAi = this.element?.dataset?.titleMode === "ai";
        if (!isSubscriptionGateEnabled()) {
            this.titleEl.textContent = this.defaultTitle;
            if (this.element?.dataset) delete this.element.dataset.titleMode;
            return;
        }
        this.titleEl.textContent =
            isFromToolbar || isMarkedAi ? this.aiTitle : this.defaultTitle;
    }

    handleHidden() {
        this.initTitles();
        if (this.titleEl) {
            this.titleEl.textContent = this.defaultTitle;
        }
        if (this.element?.dataset) {
            delete this.element.dataset.titleMode;
        }
    }

    setInitialToggleState() {
        if (this.toggle?.element) {
            this.toggle.element.checked = false;
            this.handleToggleChange();
        }
    }

    resetToggle() {
        if (this.toggle?.element) {
            this.toggle.element.checked = false;
            this.handleToggleChange();
        }
    }

    handleToggleChange() {
        const isYearly = this.toggle?.element.checked;
        if (isYearly) {
            this.addClass("active").addClass("active-live");
            this.updateSubscriptionLinks(true);
        } else {
            this.removeClass("active").removeClass("active-live");
            this.updateSubscriptionLinks(false);
        }
        this.updatePrices(isYearly);
    }

    updatePrices(isYearly) {
        this.findAll('.price-current').forEach(el => {
            el.textContent = isYearly ? el.dataset.priceYearly : el.dataset.priceMonthly;
        });
        this.findAll('.price-old').forEach(el => {
            // в годовом режиме зачёркиваем месячную цену, в месячном — полную цену до скидки за переход
            const oldPrice = isYearly ? el.dataset.priceOld : el.dataset.priceOldMonthly;
            if (oldPrice) {
                el.textContent = oldPrice;
                el.classList.remove('d-none');
            } else {
                el.classList.add('d-none');
            }
        });
        this.findAll('.min[data-cost-monthly]').forEach(el => {
            el.textContent = isYearly ? el.dataset.costYearly : el.dataset.costMonthly;
        });
    }

    updateSubscriptionLinks(addProlongation) {
        const buttons = this.findAll("a.subscription-button");

        buttons.forEach((button) => {
            let href = button.getAttribute("href");

            if (addProlongation) {
                href =
                    href +
                    (href.includes("?") ? "&" : "?") +
                    "prolongation=yearly";
            } else {
                href = href.replace(/[?&]prolongation=yearly/, "");
            }

            button.setAttribute("href", href);

            const isMonthlyAvailable =
                button.getAttribute("data-btn_monthly") === "1";
            const isYearlyAvailable =
                button.getAttribute("data-btn_yearly") === "1";

            const currentBtnTitle = button.querySelector(".current-btn-title");
            const buyBtnTitle = button.querySelector(".buy-btn-title");

            if (
                (addProlongation && isYearlyAvailable) ||
                (!addProlongation && isMonthlyAvailable)
            ) {
                button.classList.add("enable-btn");
                button.classList.remove("disable-btn");
                currentBtnTitle.classList.add("d-none");
                buyBtnTitle.classList.remove("d-none");
            } else {
                button.classList.add("disable-btn");
                button.classList.remove("enable-btn");
                buyBtnTitle.classList.add("d-none");
                currentBtnTitle.classList.remove("d-none");
            }
        });
    }

    handlePayment(event, button) {
        event?.preventDefault();

        const targetButton = button || event?.currentTarget;
        const href = targetButton?.getAttribute("href");

        if (!href) {
            return;
        }

        if (Number(this.available_region)) {
            window.location.href = href;
            return;
        }

        this.regionPaymentModal?.show(href);
    }
}
